import * as React from 'react';
import { comparisonInput } from '../configurationStyles';

import {
  ComparisonSpecType,
  DateGranularity,
  dateTruncFromGranularity,
  HistoricalComparisonSpec,
  isHistoricalComparisonSpec,
  isMarkBasedComparison,
  isValidHistoricalComparisonInput,
  makeMarkBasedOrHistoricalComparisonSpec,
  MarkBasedComparisonSpec,
  MAXIMUM_COMPARISON_VALUE,
  MINIMUM_COMPARISON_VALUE,
  shouldShowComparisonLine,
} from '@tableau/metrics-engine';
import { CheckBoxWithLabelWidget, TextFieldWidget } from '@tableau/widgets';
import { Messages } from '../../../../gen-src/Localize';
import { shakeInputIfInvalid } from '../../../shakeIfInvalid/shakeInputIfInvalid';
import { labelForGranularity } from '../../../utils';

export interface HistoricalComparisonProps {
  validationCounter: number;

  granularity: DateGranularity;

  selectedComparison: ComparisonSpecType | null;
  onSelectedComparisonChange: (comparison: HistoricalComparisonSpec | MarkBasedComparisonSpec | null) => void;

  disabled?: boolean;
}

const getNumberOfUnits = (spec: HistoricalComparisonSpec | MarkBasedComparisonSpec | null): number | undefined => {
  // This returns 1 in the case that the configuration component is initially rendered with
  // a null comparison. When that happens, we'd like to "default" to a default historical
  // comparison and not start in an error state.
  if (spec === null) {
    return 1;
  }

  if (isHistoricalComparisonSpec(spec)) {
    return spec.numberOfPeriods ?? 1;
  }

  return spec.numberOfMarks;
};

export const HistoricalComparisonComponent = (props: HistoricalComparisonProps): JSX.Element | null => {
  if (
    props.selectedComparison !== null &&
    !isHistoricalComparisonSpec(props.selectedComparison) &&
    !isMarkBasedComparison(props.selectedComparison)
  ) {
    return null;
  }

  const [hasBeenEdited, setEdited] = React.useState(false);

  const [numberOfUnits, setNumberOfUnits] = React.useState(getNumberOfUnits(props.selectedComparison));
  const [showComparisonLine, setShowComparisonLine] = React.useState(
    shouldShowComparisonLine(props.selectedComparison)
  );

  const updateStateValue = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEdited(true);
    const reference = parseInt(e.target.value, 10);
    let value = isNaN(reference) ? undefined : reference;
    setNumberOfUnits(value);
  };

  const handleShowComparisonLineToggle = (checked: boolean) => {
    setShowComparisonLine(checked);
  };

  React.useEffect(() => {
    if (numberOfUnits !== undefined) {
      props.onSelectedComparisonChange(
        makeMarkBasedOrHistoricalComparisonSpec(
          numberOfUnits ?? undefined,
          dateTruncFromGranularity(props.granularity),
          showComparisonLine
        )
      );
    } else {
      props.onSelectedComparisonChange(null);
    }
  }, [numberOfUnits, showComparisonLine]);

  const onBlur = () => {
    setEdited(true);
  };

  const clearValue = () => {
    setNumberOfUnits(undefined);
  };

  const invalid = hasBeenEdited && !isValidHistoricalComparisonInput(numberOfUnits ?? null);

  const inputRef = React.createRef<HTMLInputElement>();
  // On validation error, the widget shakes to grab the user's attention if it
  // doesn't have a valid value.  For this particular input, we only want to do
  // this if the value has been edited, since we don't want to an error state on
  // initial render.
  React.useEffect(shakeInputIfInvalid(invalid, inputRef), [props.validationCounter]);
  const comparisonLineDisabled = !!props.disabled || props.granularity === DateGranularity.UNKNOWN;
  return (
    <>
      <TextFieldWidget
        className={comparisonInput}
        label={<span style={{ fontWeight: 'bold' }}>{labelForGranularity(props.granularity)}</span>}
        testId="metric-granularity-units"
        onChange={updateStateValue}
        value={numberOfUnits ?? ''}
        onClear={clearValue}
        onBlur={onBlur}
        valid={invalid ? false : undefined}
        message={invalid ? Messages.HistoricalComparisonInputErrorMessage() : <br />}
        ref={inputRef}
        min={MINIMUM_COMPARISON_VALUE}
        max={MAXIMUM_COMPARISON_VALUE}
        disabled={props.disabled}
      />
      <CheckBoxWithLabelWidget
        label={Messages.ShowComparisonLineToggleLabel()}
        testId="metric-show-comparison-line"
        checked={showComparisonLine ?? false}
        handleChange={handleShowComparisonLineToggle}
        removeLeftMargin={true}
        disabled={comparisonLineDisabled}
        style={{ fontWeight: 'normal', marginBottom: '10px' }}
      />
      <div />
    </>
  );
};
